/**
 * Profile Feature Constants
 * 
 * Constants and configuration for profile management
 */

import { SubscriptionPlan, SubscriptionStatus } from './types';

/**
 * Subscription plans configuration
 */
export const SUBSCRIPTION_PLANS: Record<SubscriptionStatus, SubscriptionPlan> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Basic features to get started',
    price: '0',
    currency: 'EUR',
    interval: 'month',
    features: [
      'Up to 5 devices',
      'Basic analytics',
      'Standard support',
      'Email notifications',
      '1 GB storage',
    ],
    limits: {
      devices: 5,
      users: 1,
      storage: '1 GB',
      apiCalls: 1000,
    },
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    description: 'Advanced features for professional users',
    price: '19.99',
    currency: 'EUR',
    interval: 'month',
    features: [
      'Unlimited devices',
      'AI-powered insights',
      'Advanced analytics',
      'API access',
      'Priority support',
      'Custom integrations',
      '50 GB storage',
      'Data export',
    ],
    limits: {
      devices: 'unlimited',
      users: 5,
      storage: '50 GB',
      apiCalls: 100000,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'All features for enterprises',
    price: 'Custom',
    currency: 'EUR',
    interval: 'month',
    features: [
      'Everything in Premium',
      'Unlimited users',
      'Unlimited devices',
      'Dedicated support',
      'Custom SLA',
      'On-premise deployment',
      'Advanced security',
      'Unlimited storage',
      'White-label options',
      '24/7 phone support',
    ],
    limits: {
      devices: 'unlimited',
      users: 'unlimited',
      storage: 'Unlimited',
      apiCalls: 'unlimited',
    },
  },
};

/**
 * Avatar upload configuration
 */
export const AVATAR_UPLOAD_CONFIG = {
  maxSize: 5 * 1024 * 1024, // 5MB
  acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  acceptedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  bucketName: 'avatars',
  defaultAvatar: '/default-avatar.png',
} as const;

/**
 * Phone number validation regex
 * Supports international format: +[country code][number]
 */
export const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

/**
 * Password requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?',
} as const;

/**
 * Password validation regex
 */
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

/**
 * Supported timezones
 */
export const TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
] as const;

/**
 * Supported languages
 */
export const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
] as const;

/**
 * Notification frequency options
 */
export const NOTIFICATION_FREQUENCIES = [
  { value: 'instant', label: 'Instant' },
  { value: 'hourly', label: 'Hourly digest' },
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' },
] as const;

/**
 * Activity log action types
 */
export const ACTIVITY_ACTIONS = {
  // Auth actions
  LOGIN: 'login',
  LOGOUT: 'logout',
  PASSWORD_CHANGED: 'password_changed',
  TWO_FACTOR_ENABLED: '2fa_enabled',
  TWO_FACTOR_DISABLED: '2fa_disabled',
  
  // Profile actions
  PROFILE_UPDATED: 'profile_updated',
  AVATAR_UPLOADED: 'avatar_uploaded',
  
  // Subscription actions
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  
  // API actions
  API_KEY_CREATED: 'api_key_created',
  API_KEY_REVOKED: 'api_key_revoked',
  
  // Data actions
  DATA_EXPORTED: 'data_exported',
  ACCOUNT_DELETED: 'account_deleted',
} as const;

/**
 * API key scopes
 */
export const API_KEY_SCOPES = [
  { value: 'devices:read', label: 'Read devices', description: 'View device information' },
  { value: 'devices:write', label: 'Write devices', description: 'Create and update devices' },
  { value: 'alarms:read', label: 'Read alarms', description: 'View alarm information' },
  { value: 'alarms:write', label: 'Write alarms', description: 'Acknowledge and resolve alarms' },
  { value: 'tasks:read', label: 'Read tasks', description: 'View tasks' },
  { value: 'tasks:write', label: 'Write tasks', description: 'Create and update tasks' },
  { value: 'telemetry:read', label: 'Read telemetry', description: 'Access device telemetry data' },
  { value: 'analytics:read', label: 'Read analytics', description: 'Access analytics and reports' },
] as const;

/**
 * Profile validation limits
 */
export const PROFILE_LIMITS = {
  fullNameMaxLength: 255,
  bioMaxLength: 1000,
  companyMaxLength: 255,
  jobTitleMaxLength: 255,
  phoneMaxLength: 20,
} as const;

/**
 * Session expiry times
 */
export const SESSION_CONFIG = {
  maxActiveSessions: 5,
  sessionTimeout: 30 * 24 * 60 * 60 * 1000, // 30 days in milliseconds
  activityTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
} as const;

/**
 * Account deletion confirmation text
 */
export const ACCOUNT_DELETION_CONFIRMATION = 'DELETE';

/**
 * Profile tab identifiers
 */
export const PROFILE_TABS = {
  GENERAL: 'general',
  SECURITY: 'security',
  SUBSCRIPTION: 'subscription',
  NOTIFICATIONS: 'notifications',
  API_KEYS: 'api-keys',
  BILLING: 'billing',
  PRIVACY: 'privacy',
} as const;

/**
 * React Query cache keys
 */
export const PROFILE_QUERY_KEYS = {
  profile: ['profile'] as const,
  sessions: ['profile', 'sessions'] as const,
  activityLog: ['profile', 'activity'] as const,
  notificationPreferences: ['profile', 'notifications'] as const,
  apiKeys: ['profile', 'api-keys'] as const,
  paymentMethods: ['profile', 'payment-methods'] as const,
  subscriptionHistory: ['profile', 'subscription-history'] as const,
  twoFactorStatus: ['profile', '2fa-status'] as const,
} as const;

/**
 * Stripe public key (to be set via env variable)
 */
export const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY || '';

/**
 * Feature flags for profile
 */
export const PROFILE_FEATURES = {
  twoFactorAuth: true,
  sessionManagement: true,
  apiKeys: true,
  paymentIntegration: true, // Will be false until Stripe is fully integrated
  dataExport: true,
  accountDeletion: true,
} as const;

